import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { getFetchLedgerLot, getFetchLedgerLotHistory } from "../Redux/products/slice";
import { BASE_URL } from '../Redux/apiConfig';
import { Timeline, Text, Title, Center, Button, Loader, Badge, Group, MantineProvider, Card } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDolly, 
  faGears, 
  faCashRegister, 
  faCircleInfo, 
  faFileCirclePlus,
  faHandshake,
  faSignature,
  faFileContract,
  faLeaf
} from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery } from '@mantine/hooks';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Column = ({ children, style }) => (
  <div style={{ width: '100%', ...style }}>{children}</div>
);

const getAllImages = async (value) => {
  if (!value?.productRefID && !value?.product_name_id) return [];

  const images = [];
  const baseUrl = 'https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/';
  const productID = value.productRefID;
  
  // Try main image
  const mainImage = `${baseUrl}${productID}.png`;
  try {
    const response = await fetch(mainImage, { method: 'HEAD' });
    if (response.ok) {
      images.push(mainImage);
    }
  } catch (error) {
    console.log('Main image not found');
  }
  
  // Try numbered images (_1, _2, _3, etc.)
  for (let i = 1; i <= 10; i++) {
    const imageUrl = `${baseUrl}${productID}_${i}.png`;
    try {
      const response = await fetch(imageUrl, { method: 'HEAD' });
      if (response.ok) {
        images.push(imageUrl);
      } else {
        break; // Stop checking if one doesn't exist
      }
    } catch (error) {
      break;
    }
  }
  
  // If no images found, use default image
  if (images.length === 0 && value.product_name_id) {
    const defaultImage = `${baseUrl}${value.product_name_id}.jpg`;
    images.push(defaultImage);
  }
  
  return images;
};

const ProductState = ({ product }) => {
  const [productImages, setProductImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [translatedFieldNames, setTranslatedFieldNames] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchImages = async () => {
      if (!product?.result?.ProductID) return;

      const images = await getAllImages({
        productRefID: product.result.ProductID,
        product_name_id: product.result.ProductNameId
      });
      
      if (isMounted) {
        setProductImages(images);
        setImageLoading(false);
      }
    };

    fetchImages();

    return () => {
      isMounted = false;
    };
  }, [product?.result?.ProductID, product?.result?.ProductNameId]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  // Get selected language from Redux
  const selectedLanguage = useSelector((state) => state.language);

  // Translate parsed field names
  useEffect(() => {
    const translateFieldNames = async () => {
      const parsedFields = product?.result?._parsedFields || [];
      if (parsedFields.length === 0 || !selectedLanguage) return;

      try {
        const response = await fetch(`${BASE_URL}/api/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            texts: parsedFields,
            targetLang: selectedLanguage,
            autoDetect: true
          })
        });

        const data = await response.json();
        
        // Create mapping: original name -> translated name
        const mapping = {};
        parsedFields.forEach((field, index) => {
          mapping[field] = data.translations[index];
        });
        
        setTranslatedFieldNames(mapping);
      } catch (error) {
        console.error('Translation error:', error);
      }
    };

    translateFieldNames();
  }, [product?.result?._parsedFields, selectedLanguage]);

  const getTranslationKey = (key) => {
    const keyMap = {
      "Owner Address": "ownerAddress",
      "UPC": "upc",
      "ProductNotes": "productNotes",
      "PhysicalState": "physicalState",
      "Quantity": "quantity",
      "PhysicalStatePeriod": "physicalStatePeriod",
      "PhysicalStateLocation": "physicalStateLocation"
    };
    return keyMap[key] || key;
  };

  const ownerDetails = {
    "Owner Address": product?.result?.OwnerPublicAddress,
    "User Name": product?.result?.UserName,
  };

  const excludedFields = [
    "OwnerPublicAddress",
    "UserName",
    "UPC", 
    "ProductID", 
    "ParentProductID", 
    "SKU", 
    "darkmode", 
    "ProductNameId",
    "Category",
    "_parsedFields" // This is just metadata, don't display it
  ];

  // Get the list of parsed field names
  const parsedFields = product?.result?._parsedFields || [];

  // Get all entries and filter out excluded fields (case-insensitive)
  const allEntries = Object.entries(product?.result || {})
    .filter(([key]) => !excludedFields.some(field => field.toLowerCase() === key.toLowerCase()));

  // Separate entries into three groups
  const productNameEntry = allEntries.filter(([key]) => key === "ProductName");
  const parsedEntries = allEntries.filter(([key]) => parsedFields.includes(key));
  const otherEntries = allEntries.filter(([key]) => key !== "ProductName" && !parsedFields.includes(key));

  // Combine in order: ProductName, parsed fields, then other fields
  const productSpecifications = [
    ...productNameEntry,
    ...parsedEntries,
    ...otherEntries
  ].map(([key, value]) => ({ label: key, value }));

  // Helper function to get translation or fall back to the original label
  const intl = useIntl();
  const getTranslatedLabel = (label) => {
    // If it's a parsed field and we have a translation, use it
    if (translatedFieldNames[label]) {
      return translatedFieldNames[label];
    }

    // Map specific keys to existing translation keys
    const existingTranslationMap = {
      "ProductName": "product.name",
      "ProductNotes": "product.descriptnotes",
      "Quantity": "product.quantity"
    };
    
    // Check if we should use an existing translation
    if (existingTranslationMap[label]) {
      return <FormattedMessage id={existingTranslationMap[label]} />;
    }
    
    const translationKey = `explorer.specification.${getTranslationKey(label)}`;
    
    // Check if translation exists
    const hasTranslation = intl.messages[translationKey] !== undefined;
    
    if (hasTranslation) {
      return <FormattedMessage id={translationKey} />;
    } else {
      // Fall back to the original label if translation doesn't exist
      return label;
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <Card withBorder radius="md" style={{ marginBottom: "20px", overflow: 'hidden' }}>
        <Card.Section style={{ borderBottom: '2px solid #dde3ea', background: '#f8fafc' }}>
          <Title order={5} style={{ padding: "14px 20px", fontWeight: 700, letterSpacing: '0.01em', color: '#1a2332' }}>
            <FormattedMessage id="explorer.owner.title" />
          </Title>
        </Card.Section>
        <div style={{ padding: "0 0 0" }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {Object.entries(ownerDetails).map(([key, value], idx) => {
                const translationKeyMap = { "User Name": "userInfo.name" };
                const translationKey = translationKeyMap[key]
                  ? translationKeyMap[key]
                  : `explorer.owner.${getTranslationKey(key)}`;
                return (
                  <tr key={key} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem', fontWeight: 600, color: '#374151', borderBottom: '1px solid #eef0f3', verticalAlign: 'top' }}>
                      <FormattedMessage id={translationKey} />
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem', color: '#6b7280', borderBottom: '1px solid #eef0f3', verticalAlign: 'top', wordBreak: 'break-word' }}>
                      {value || 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card withBorder radius="md" style={{ overflow: 'hidden' }}>
        <Card.Section style={{ borderBottom: '2px solid #dde3ea', background: '#f8fafc' }}>
          <Title order={5} style={{ padding: "14px 20px", fontWeight: 700, letterSpacing: '0.01em', color: '#1a2332' }}>
            <FormattedMessage id="explorer.specification.title" />
          </Title>
        </Card.Section>
        
        {/* Product Images Carousel */}
        {imageLoading ? (
          <Center style={{ height: 300, marginTop: 20 }}>
            <Loader size="lg" />
          </Center>
        ) : productImages.length > 0 ? (
          <div style={{ position: 'relative', width: '100%', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img 
                src={productImages[currentImageIndex]} 
                alt="Product" 
                style={{        
                  width: '300px',  // CHANGED: from 400px to 300px
                  height: '300px', // CHANGED: from 400px to 300px
                  borderRadius: '12px',
                  objectFit: 'cover',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }} 
              />
            </div>
            
            {/* Navigation arrows - only show if multiple images */}
            {productImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    prevImage();
                  }}
                  style={{
                    position: 'absolute',
                    left: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 100
                  }}
                >
                  <FaChevronLeft size={16} />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    nextImage();
                  }}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 100
                  }}
                >
                  <FaChevronRight size={16} />
                </button>
                
                {/* Image counter */}
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  padding: '3px 8px',
                  borderRadius: '10px',
                  fontSize: '10px',
                  fontWeight: 500,
                  zIndex: 100,
                  whiteSpace: 'nowrap',
                  width: 'fit-content',
                  height: 'fit-content',
                  display: 'inline-block',
                  lineHeight: '1.2'
                }}>
                  {currentImageIndex + 1} / {productImages.length}
                </div>
              </>
            )}
          </div>
        ) : null}
        
        <div style={{ padding: '0 0 0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {productSpecifications.map(({ label, value }, idx) => (
                <tr key={label} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ padding: '12px 20px', fontSize: '0.85rem', fontWeight: 600, color: '#374151', borderBottom: '1px solid #eef0f3', verticalAlign: 'top' }}>
                    {getTranslatedLabel(label)}
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: '0.85rem', color: '#6b7280', borderBottom: '1px solid #eef0f3', verticalAlign: 'top', wordBreak: 'break-word', whiteSpace: label === 'ProductNotes' ? 'pre-wrap' : 'normal' }}>
                    {label === 'Quantity' && value && value.includes(' ') ? (
                      <>
                        {value.split(' ')[0]}{' '}
                        <Badge size="sm" variant="light" color="blue">
                          {value.substring(value.indexOf(' ') + 1)}
                        </Badge>
                      </>
                    ) : (
                      value || 'N/A'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

function DualTimeline({ productHist }) {
  const transactionMethods = ['addCommercialContract', 'liftAllSubject', 'sellerSign', 'buyerSign'];
  const network = BASE_URL.includes('vertobackendappdev') ? 'testnet' : 'mainnet';
  const isMobile = useMediaQuery('(max-width: 768px)');

  const processEvents = () => {
    if (!productHist || typeof productHist !== 'object') {
      console.error("productHist is undefined or not an object:", productHist);
      return [];
    }
  
    const allEvents = [];
  
    Object.entries(productHist).forEach(([timestamp, item], index) => {
      let method;
      if (index === 0) {
        method = "productcreation";
      } else if (item.method || item.event) {
        method = item.method || item.event;
      } else if (!item.physicalState || item.physicalState==='') {
        method = "transportLot";
      } else {
        method = "transformLot";
      }
      if (!method) return;
  
      const eventData = {
        timestamp,
        method,
        period: item.Period || item.period,
        location: item.location,
        physicalState: item.physicalState,
        id: item.id,
        transportDescription: item.transportDescription,
        ownerAccountId: item.ownerAccountId,
        _ts: item._ts,
        isTransaction: transactionMethods.includes(method),
        price: item.price
      };
  
      allEvents.push(eventData);
    });
  
    return allEvents.sort((a, b) => (a._ts || 0) - (b._ts || 0));
  };  

  const sortedEvents = processEvents();

  const getIcon = (method, isTransaction) => {
    let icon;
    if (isTransaction) {
      switch (method) {
        case 'addCommercialContract':
          icon = faFileContract;
          break;
        case 'sellerSign':
        case 'buyerSign':
          icon = faSignature;
          break;
        case 'liftAllSubject':
          icon = faHandshake;
          break;
        default:
          icon = faCircleInfo;
      }
    } else {
      switch (method) {
        case 'productcreation':
          icon = faFileCirclePlus;
          break;
        case 'transform':
        case 'transformLot':
          icon = faGears;
          break;
        case 'transport':
        case 'transportLot':
          icon = faDolly;
          break;
        case 'transferOwner':
          icon = faCashRegister;
          break;
        default:
          icon = faCircleInfo;
      }
    }
    return icon;
  };

  const renderTimelineItem = (item, index) => (
    <Timeline.Item
      key={index}
      bullet={
        <FontAwesomeIcon 
          icon={getIcon(item.method, item.isTransaction)} 
          style={{ color: item.isTransaction ? 'white' : undefined }} 
        />
      }
      title={<FormattedMessage id={`explorer.timeline.${item.method}`} />}
      color={item.isTransaction ? "orange" : "blue"}
    >
      <Text color="dimmed" size="sm">
        {item._ts && (
          <>
            <FormattedMessage id="product.dateon1" /> {new Date(item._ts * 1000).toLocaleString()}
            <br />
          </>
        )}
        {item.physicalState && !item.isTransaction && (
          <>
            <FormattedMessage id="explorer.timeline.process" /> {item.physicalState}
            <br />
          </>
        )}
        {item.period && (
          <>
            <FormattedMessage id="product.period1" /> {item.period}
            <br />
          </>
        )}
        {item.location && !item.isTransaction && (
          <>
            <FormattedMessage id="product.location1" /> {item.location}
            <br />
          </>
        )}
        {item.transportDescription && !item.isTransaction && (
          <>
            <FormattedMessage id="product.description1" /> {item.transportDescription}
            <br />
          </>
        )}
        {item.id && (
          <Button
            component="a"
            href={`https://hashscan.io/${network}/${item.ownerAccountId ? 'account' : 'transaction'}/${item.ownerAccountId || item.id}`}
            target="_blank"
            rel="noopener noreferrer"
            size="xs"
            variant="subtle"
            color={item.isTransaction ? "orange" : "blue"}
          >
            <FormattedMessage id="certificate.linktoledger.hedera" />
          </Button>
        )}
      </Text>
    </Timeline.Item>
  );

  const titleStyle = {
    fontFamily: 'Montserrat, sans-serif',
    marginBottom: '2rem',
    marginTop: '1.5rem'
  };

  const renderChronologicalTimeline = () => {
    const allEvents = sortedEvents.map((item, index) => ({
      ...item,
      position: item.isTransaction ? 'right' : 'left'  // Ensure transactions go right
    }));

    return (
      <div style={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Column Headers */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '2rem'
        }}>
          <div style={{ width: isMobile ? '100%' : '45%', textAlign: 'center' }}>
            <h5 style={titleStyle}>
              <FormattedMessage id="certificate.history" />
            </h5>
          </div>
          <div style={{ width: isMobile ? '100%' : '45%', textAlign: 'center' }}>
            <h5 style={titleStyle}>
              <FormattedMessage id="button.comercontract" />:
            </h5>
          </div>
        </div>

        {/* Timeline Content */}
        <div style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}>
          {/* Central line */}
          <div style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            bottom: 0,
            width: '2px',
            backgroundColor: '#e9ecef',
            transform: 'translateX(-50%)'
          }} />

          <div style={{ width: '100%' }}>
            {allEvents.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: item.position === 'right' ? 'flex-end' : 'flex-start',
                  padding: '3px 0',
                  position: 'relative',
                  width: '100%'
                }}
              >
                <div
                  style={{
                    width: isMobile ? '100%' : '45%',
                    margin: item.position === 'right' ? '0 0 0 10px' : '0 10px 0 0',
                    position: 'relative'
                  }}
                >
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '15px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    position: 'relative'
                  }}>
                    {/* Rest of the content remains the same */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px'
                    }}>
                      <FontAwesomeIcon 
                        icon={getIcon(item.method, item.isTransaction)} 
                        style={{ 
                          color: 'white',
                          backgroundColor: item.isTransaction ? '#fd7e14' : '#228be6',
                          padding: '8px',
                          borderRadius: '50%',
                          marginRight: '10px'
                        }} 
                      />
                      <h6 style={{ margin: 0 }}>
                        <FormattedMessage id={`explorer.timeline.${item.method}`} />
                      </h6>
                    </div>

                    <Text color="dimmed" size="sm">
                      {item._ts && (
                        <>
                          <FormattedMessage id="product.dateon1" /> {new Date(item._ts * 1000).toLocaleString()}
                          <br />
                        </>
                      )}
                      {item.physicalState && !item.isTransaction && (
                        <>
                          <FormattedMessage id="explorer.timeline.process" /> {item.physicalState}
                          <br />
                        </>
                      )}
                      {item.period && (
                        <>
                          <FormattedMessage id="product.period1" /> {item.period}
                          <br />
                        </>
                      )}
                      {item.location && !item.isTransaction && (
                        <>
                          <FormattedMessage id="product.location1" /> {item.location}
                          <br />
                        </>
                      )}
                      {item.transportDescription && !item.isTransaction && (
                        <>
                          <FormattedMessage id="product.description1" /> {item.transportDescription}
                          <br />
                        </>
                      )}
                      {item.isTransaction && (
                        <>
                          <FormattedMessage id="offer1.priceTitle" /> {item.Price || '-'}
                          <br />
                        </>
                      )}
                      {item.id && (
                        <Button
                          component="a"
                          href={`https://hashscan.io/${network}/${item.ownerAccountId ? 'account' : 'transaction'}/${item.ownerAccountId || item.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          variant="subtle"
                          color={item.isTransaction ? "orange" : "blue"}
                        >
                          <FormattedMessage id="certificate.linktoledger.hedera" />
                        </Button>
                      )}
                    </Text>
                  </div>

                  {/* Timeline connector and dot */}
                  <div style={{
                    position: 'absolute',
                    top: '30px',
                    [item.isTransaction ? 'left' : 'right']: '-6px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: item.isTransaction ? '#fd7e14' : '#228be6',
                    border: '2px solid white',
                    zIndex: 2
                  }} />
                  
                  {/* Horizontal connector line */}
                  <div style={{
                    position: 'absolute',
                    top: '35px',
                    [item.isTransaction ? 'left' : 'right']: '5px',
                    width: '10px',
                    height: '2px',
                    backgroundColor: item.isTransaction ? '#fd7e14' : '#228be6'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      padding: isMobile ? '5px' : '10px 0',
    }}>
      <div style={{
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        padding: isMobile ? '5px' : '15px'
      }}>
        {isMobile ? (
          // Mobile view remains the same
          <div style={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '5px'
          }}>
            <h5 style={titleStyle}>
              <FormattedMessage id="certificate.history" />
            </h5>
            <Timeline 
              active={sortedEvents.length}
              bulletSize={20} 
              lineWidth={2}
              styles={(theme) => ({
                root: {
                  paddingTop: '1rem',
                  paddingBottom: '2rem'
                },
                item: {
                  backgroundColor: 'transparent',
                  paddingBottom: '10px',
                  minHeight: '180px',
                }
              })}
            >
              {sortedEvents.map((item, index) => renderTimelineItem(item, index))}
            </Timeline>
          </div>
        ) : (
          // New chronological desktop view
          <div style={{
            width: '100%',
            padding: '10px'
          }}>
            {renderChronologicalTimeline()}
          </div>
        )}
      </div>
    </div>
  );
}

const CertificatePage = ({ productId, fromExplorer = false }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [productDict, setProductDict] = useState({});
  const [productHist, setProductHist] = useState({});
  const [transportCarbon, setTransportCarbon] = useState(null);
  const dispatch = useDispatch();
  const fetchLedgerLot = useSelector((state) => state.products.fetchLedgerLot);
  const fetchLedgerLotHistory = useSelector((state) => state.products.fetchLedgerLotHistory);
  const intl = useIntl();
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState("");
  const [isSpecialAccount, setIsSpecialAccount] = useState(false);
  const selectedLanguage = useSelector((state) => state.language);

  // Use either URL param or prop
  const effectiveId = fromExplorer ? productId : id;

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveId) return;

      // Only fetch if we don't have data or if the ID changed
      if (!fetchLedgerLot?.result?.ProductID || 
          !fetchLedgerLotHistory?.result ||
          String(fetchLedgerLot.result.ProductID) !== String(effectiveId)) {
        
        setIsLoading(true);
        try {
          await dispatch(getFetchLedgerLot({ inputValue: effectiveId }));
          await dispatch(getFetchLedgerLotHistory({ inputValue: effectiveId }));
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [effectiveId, dispatch]);

  // Process data after fetch
  useEffect(() => {
    if (!fetchLedgerLot?.result) return;

    const product = fetchLedgerLot.result;
    
    setIsSpecialAccount(product?.darkmode === 1);

    if (product?.ProductName) {
      const [mainProductName, ...rest] = product.ProductName.split(",");
      const additionalFields = {};
      const parsedFieldNames = []; // Track which fields came from ProductName

      if (rest.length > 0) {
        rest.join(",").split("\\r\\n").forEach((line) => {
          if (!line) return;
          const [key, value] = line.split(":").map((str) => str?.trim());
          if (key && value) {
            additionalFields[key] = value;
            parsedFieldNames.push(key); // Remember this was parsed
          }
        });
      }

      setProductDict({
        ...fetchLedgerLot,
        result: {
          ...product,
          ProductName: mainProductName?.trim() || product.ProductName,
          ...additionalFields,
          _parsedFields: parsedFieldNames, // Store list of parsed field names
        },
      });
    } else {
      setProductDict(fetchLedgerLot);
    }

    if (fetchLedgerLotHistory) {
      setProductHist(fetchLedgerLotHistory);
    }

    if (product.ProductID) {
      setQrCodeImageUrl(`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/qrcode/${product.ProductID}.png`);
    }
  }, [fetchLedgerLot, fetchLedgerLotHistory]);

  useEffect(() => {
    const fetchData = async () => {
      if (productDict && productDict.result && productDict.result.ProductID) {
        try {
          const productResponse = await fetch(
            `${BASE_URL}/getProductCertificate?product_id=${productDict.result.ProductID}&targetLanguage=${selectedLanguage}`
          );

          if (productResponse.ok) {
            // G�� Parse the JSON response
            const data = await productResponse.json();
            
            // G�� Use the qrCode data from the response
            if (data.qrCode) {
              setQrCodeImageUrl(data.qrCode); // This is the base64 data URL
            }
          }
        } catch (error) {
          console.error(error);
        }
        setIsLoading(false);
      }
    };

    fetchData();
  }, [productDict?.result?.ProductID, selectedLanguage]);

  useEffect(() => {
    const fetchTransportCarbon = async () => {
      if (!productDict?.result?.ProductID) return;

      try {
        const response = await fetch(
          `${BASE_URL}/getProductTransportCarbon?product_id=${productDict.result.ProductID}&containerSize=20FT`
        );

        if (!response.ok) {
          setTransportCarbon(null);
          return;
        }

        const data = await response.json();
        setTransportCarbon(data?.result || null);
      } catch (error) {
        console.error('Error loading transport carbon:', error);
        setTransportCarbon(null);
      }
    };

    fetchTransportCarbon();
  }, [productDict?.result?.ProductID]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <MantineProvider theme={{ colorScheme: isSpecialAccount ? 'dark' : 'light' }}>
      <div
        style={{
          padding: isMobile ? '3px' : '30px',
          background: isSpecialAccount
            ? 'linear-gradient(to bottom, #121212, #1A1B1E)'
            : '#f9f9f9',
          minHeight: '100vh',
        }}
      >
        <Title
          order={3}
          weight={200}
          align="center"
          style={{
            color: isSpecialAccount ? '#FFA726' : '#333',
            marginBottom: '20px',
          }}
        >
          {intl.formatMessage({ id: 'explorer.title' })}
        </Title>
          {isLoading ? (
            <Center><Loader /></Center>
          ) : (
            <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* QR Code */}
              {qrCodeImageUrl && (
                <div style={{ textAlign: 'center' }}>
                  <img src={qrCodeImageUrl} alt="QR Code" style={{ maxWidth: '180px' }} />
                </div>
              )}
              {/* Owner + Specs stacked inside ProductState */}
              <div>
                <Title order={4} weight={600} style={{ marginBottom: '12px', color: isSpecialAccount ? '#FFA726' : '#333' }}>
                  <FormattedMessage id="explorer.section.pinfo" />
                </Title>
                <ProductState product={productDict}/>
              </div>
              {/* Transport carbon summary */}
              {transportCarbon?.summary && (
                <div>
                  <Title order={4} weight={600} style={{ marginBottom: '12px', color: isSpecialAccount ? '#FFA726' : '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faLeaf} style={{ color: '#2f9e44' }} />
                    <FormattedMessage id="certificate.carbon.title" />
                  </Title>
                  <Card withBorder radius="md" style={{ overflow: 'hidden' }}>
                    <Card.Section style={{ borderBottom: '2px solid #dde3ea', background: '#f8fafc' }}>
                      <Title order={5} style={{ padding: '14px 20px', fontWeight: 700, letterSpacing: '0.01em', color: '#1a2332' }}>
                        <FormattedMessage id="certificate.carbon.subtitle" />
                      </Title>
                    </Card.Section>
                    <div style={{ padding: '16px 20px', display: 'grid', rowGap: '10px' }}>
                      <Text size="sm"><strong><FormattedMessage id="certificate.carbon.route" /></strong> {transportCarbon.summary.routeLabel}</Text>
                      <Text size="sm"><strong><FormattedMessage id="certificate.carbon.emissions" /></strong> {transportCarbon.summary.emissionsLabel}</Text>
                      <Text size="sm"><strong><FormattedMessage id="certificate.carbon.method" /></strong> {transportCarbon.summary.methodologyLabel}</Text>
                      {transportCarbon.summary.distanceLabel && (
                        <Text size="sm"><strong><FormattedMessage id="certificate.carbon.distance" /></strong> {transportCarbon.summary.distanceLabel}</Text>
                      )}
                      {transportCarbon.summary.containerSize && (
                        <Group spacing="xs">
                          <Text size="sm"><strong><FormattedMessage id="certificate.carbon.container" /></strong></Text>
                          <Badge color="green" variant="light">{transportCarbon.summary.containerSize}</Badge>
                        </Group>
                      )}
                    </div>
                  </Card>
                </div>
              )}
              {/* History */}
              <div>
                <Title order={4} weight={600} style={{ marginBottom: '12px', color: isSpecialAccount ? '#FFA726' : '#333' }}>
                  <FormattedMessage id="explorer.section.phistory" />
                </Title>
                <DualTimeline productHist={productHist} />
              </div>
            </div>
          )}
      </div>
    </MantineProvider>
  );
};

export default CertificatePage;
